import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { quickQuoteApi } from '../api/endpoints.ts';
import { useAiDisclosure } from './ai-disclosure.tsx';
import { ApiRequestError } from '../api/client.ts';
import i18n, { activeLanguage } from '../i18n/index.ts';
import {
  clearActiveQuickQuote,
  clearPendingQuickQuote,
  loadActiveQuickQuote,
  loadPendingQuickQuote,
  pendingQuickQuoteIsSettled,
  rememberActiveQuickQuote,
  rememberPendingQuickQuote,
  type QuickQuoteRecoveryScope,
} from '../state/quick-quote-recovery.ts';
import { Alert, Button, LoadingBlock, TextArea, TextInput, classes } from './ui.tsx';
import { QuickQuotePhotoPicker, QuickQuoteSessionPhotos, useQuickQuotePhotos } from './quick-quote-photos.tsx';
import type {
  AnswerQuickQuoteInput,
  GenerateQuickQuoteResponse,
  QuickQuoteAnswer,
  QuickQuoteCapabilities,
  QuickQuoteCreateInput,
  QuickQuoteMeasurement,
  QuickQuoteQuestion,
  QuickQuoteRoom,
  QuickQuoteSession,
} from '@shared/quick-quote.ts';
import {
  QUICK_QUOTE_MAX_PHOTOS,
  QUICK_QUOTE_MIN_PROMPT_CHARS,
  QUICK_QUOTE_PHOTO_CREDITS,
} from '@shared/quick-quote.ts';
import type { QuoteSegment } from '@shared/constants/index.ts';

interface QuickQuoteProps {
  customerId: string;
  propertyId: string | null;
  segment: QuoteSegment;
  recoveryScope: QuickQuoteRecoveryScope;
  /** In-page, context-preserving escape to the ordinary quote builder. */
  manualQuoteHref: string;
  /**
   * What the page's ways out would leave behind: a paragraph or photos not yet
   * drafted (`unsent`, asked about first), a request in flight in this tab
   * right now (`submitting`, when only Cancel may leave: it settles in
   * seconds), or a request whose outcome is not known yet, or saved work that
   * is still being checked (`stuck`). Stuck is asked about and then allowed:
   * waiting on a server that will not answer is not a reason to keep someone
   * from building the quote by hand.
   */
  onInputStateChange?: (state: QuickQuoteInputState) => void;
  /** The recovery pointers this panel is waiting on, whenever they change. */
  onRecoveryChange?: (recovery: QuickQuoteRecoveryIds) => void;
  onGenerated: (quoteId: string) => void;
  onResumeContext: (context: { customerId: string; propertyId: string | null; segment: QuoteSegment }) => Promise<void>;
}

export type QuickQuoteInputState = 'clear' | 'unsent' | 'submitting' | 'stuck';

/**
 * The recovery pointers this panel is waiting on, so a page that lets it go
 * clears exactly those: another tab may have stored newer ones since.
 */
export interface QuickQuoteRecoveryIds {
  pendingCreateId: string | null;
  activeSessionId: string | null;
}

interface DraftAnswer {
  optionIds: string[];
  text: string;
}

type RecoveryMismatch = {
  source: 'pending' | 'active';
  pointerId: string;
  session: QuickQuoteSession;
};

const MAX_QUESTIONS = 3;
const MAX_ROOM_SUGGESTIONS = 6;
const MAX_CLARIFICATION_ROUNDS = 2;
const MAX_FOLLOW_UP_ROUNDS = 2;

const CONCLUSIVE_REFRESH_CODES = new Set([
  'QUICK_QUOTE_VERSION_CONFLICT',
  'QUICK_QUOTE_NOT_READY',
  'QUICK_QUOTE_LIBRARY_CHANGED',
  'QUICK_QUOTE_COVERAGE_CHANGED',
]);
const OPERATION_PRESERVING_LIMIT_CODES = new Set(['QUICK_QUOTE_DAILY_CAP', 'QUICK_QUOTE_DEPLOYMENT_CAP']);
/**
 * The one answer that retires a recovery pointer.
 *
 * Quick Quote's public status set is 400/403/409/429/503 — there is no 404 in
 * it — so a lookup for a request the server has no row for arrives as this
 * code at 409. Waiting for a 404 meant the pointer was never retired and every
 * later Quick Quote in this company sat behind a check that could only repeat
 * itself.
 */
const RECOVERY_POINTER_UNKNOWN = 'QUICK_QUOTE_REQUEST_UNKNOWN';

function newRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `qq_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
}

function apiMessage(caught: unknown, fallback: string): string {
  // The Worker's plain wording for its failure codes, in this page's language.
  if (caught instanceof ApiRequestError && caught.code && i18n.exists(`quickQuote.errors.${caught.code}`)) {
    return i18n.t(`quickQuote.errors.${caught.code}`);
  }
  return caught instanceof Error && caught.message ? caught.message : fallback;
}

function isApiError(caught: unknown, statusOrCode: number | string): boolean {
  if (!(caught instanceof ApiRequestError)) return false;
  return typeof statusOrCode === 'number' ? caught.status === statusOrCode : caught.code === statusOrCode;
}

/**
 * Refusals that will read the same on every later check: the request was
 * rejected, what was stored for it cannot be used, or the operation is parked
 * for a person to look at. Only these offer the deliberate way out, because
 * only these prove there is nothing left to wait for.
 *
 * Named one by one rather than taken from the HTTP status. 409 also carries the
 * conflicts worth checking again — a snapshot that moved while it was being
 * read, a library that changed — and dropping a pointer on one of those
 * discards a Quick Quote that was still recoverable; a cap, an outage or a dead
 * network says nothing about the request at all, and forgetting a pointer whose
 * parse may still be running is how the same four credits get spent twice.
 *
 * The two ambiguous outcomes ARE here, despite their names. Neither is ever
 * sent while anything is still running: the runner only raises them once it has
 * durably marked the operation for manual review, and every later lookup
 * replays that stored code, so a pointer holding one of them can only wait out
 * its own expiry.
 */
const TERMINAL_RECOVERY_CODES = new Set([
  'QUICK_QUOTE_REQUEST_REJECTED',
  'QUICK_QUOTE_PROVIDER_SCHEMA_INVALID',
  'QUICK_QUOTE_PARTIAL_REFUSED',
  'QUICK_QUOTE_STORED_REQUEST_INVALID',
  'QUICK_QUOTE_STORED_STATE_INVALID',
  'QUICK_QUOTE_STORED_SCOPE_INVALID',
  'QUICK_QUOTE_PROVIDER_AMBIGUOUS',
  'QUICK_QUOTE_RESULT_STORE_AMBIGUOUS',
]);

function isConclusiveRecoveryRefusal(caught: unknown): boolean {
  return caught instanceof ApiRequestError && TERMINAL_RECOVERY_CODES.has(caught.code ?? '');
}

function quickQuoteCapabilityProtocol(value: unknown): QuickQuoteCapabilities | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const candidate = value as Partial<QuickQuoteCapabilities>;
  const costs = candidate.costs as Partial<QuickQuoteCapabilities['costs']> | undefined;
  const limits = candidate.limits as Partial<QuickQuoteCapabilities['limits']> | undefined;
  const unavailableReason = candidate.unavailableReason;
  const unavailableReasonValid =
    unavailableReason === null ||
    unavailableReason === 'plan' ||
    unavailableReason === 'ai_off' ||
    unavailableReason === 'feature_off' ||
    unavailableReason === 'unconfigured';
  if (
    candidate.feature !== 'quick_quote' ||
    candidate.requiredPlan !== 'starter' ||
    typeof candidate.available !== 'boolean' ||
    !unavailableReasonValid ||
    (candidate.available === true && unavailableReason !== null) ||
    (candidate.available === false && unavailableReason === null) ||
    costs?.initialParse !== 4 ||
    costs.freeTextClarification !== 2 ||
    costs.structuredAnswers !== 0 ||
    costs.generate !== 0 ||
    limits?.promptChars !== 4_000 ||
    limits.answerTextChars !== 1_000 ||
    limits.maxQuestionsPerRound !== MAX_QUESTIONS ||
    limits.maxClarificationRounds !== MAX_CLARIFICATION_ROUNDS ||
    limits.maxRooms !== 40
  ) {
    return null;
  }
  // Photos are an addition: a server that does not describe them, or
  // describes them differently, leaves Quick Quote working from text alone.
  const photos = candidate.photos as Partial<QuickQuoteCapabilities['photos']> | undefined;
  const photosUsable =
    photos !== undefined &&
    photos.available === true &&
    photos.maxPhotos === QUICK_QUOTE_MAX_PHOTOS &&
    photos.creditsPerPhoto === QUICK_QUOTE_PHOTO_CREDITS;
  return {
    ...(candidate as QuickQuoteCapabilities),
    photos: photosUsable
      ? (photos as QuickQuoteCapabilities['photos'])
      : {
          available: false,
          unavailableReason: candidate.unavailableReason ?? 'unconfigured',
          maxPhotos: QUICK_QUOTE_MAX_PHOTOS,
          creditsPerPhoto: QUICK_QUOTE_PHOTO_CREDITS,
        },
  };
}

function isAmbiguous(caught: unknown): boolean {
  if (!(caught instanceof ApiRequestError)) return true;
  if (caught.status >= 500 || caught.status === 408) return true;
  if (caught.code === 'QUICK_QUOTE_REQUEST_IN_PROGRESS') return true;
  if (caught.status === 429 && OPERATION_PRESERVING_LIMIT_CODES.has(caught.code)) return true;
  // Only an unclassified conflict has an unknown outcome. Named 4xx errors
  // are conclusive and must release the latched request instead of freezing
  // the estimator behind a request the server already rejected.
  return caught.status === 409 && (!caught.code || caught.code === 'UNKNOWN' || caught.code === 'CONFLICT');
}

function requiresRefresh(caught: unknown): boolean {
  return caught instanceof ApiRequestError && Boolean(caught.code && CONCLUSIVE_REFRESH_CODES.has(caught.code));
}

function hasAnswer(answer: DraftAnswer | undefined): boolean {
  return Boolean(answer && (answer.optionIds.length > 0 || answer.text.trim()));
}

function sameContext(
  session: QuickQuoteSession,
  context: Pick<QuickQuoteProps, 'customerId' | 'propertyId' | 'segment'>,
): boolean {
  return (
    session.customerId === context.customerId &&
    session.propertyId === context.propertyId &&
    session.segment === context.segment
  );
}

function allowsPaidClarification(question: QuickQuoteQuestion): boolean {
  // Numeric entry is a structured answer even though it travels in `text`.
  return question.allowText && question.kind !== 'number';
}

function questionNeedsClarificationCredits(question: QuickQuoteQuestion, answer: DraftAnswer | undefined): boolean {
  return Boolean(answer?.text.trim()) && allowsPaidClarification(question);
}

function roomCheckProtocolError(questions: QuickQuoteQuestion[], maximum: number): boolean {
  if (questions.length > Math.min(MAX_QUESTIONS, maximum)) return true;
  const questionIds = questions.map((question) => question.id);
  if (
    questionIds.some((id) => typeof id !== 'string' || id.trim().length === 0) ||
    new Set(questionIds).size !== questionIds.length
  ) {
    return true;
  }
  for (const question of questions) {
    if (!Array.isArray(question.options)) return true;
    const optionIds = question.options.map((option) => option.id);
    if (
      optionIds.some((id) => typeof id !== 'string' || id.trim().length === 0) ||
      new Set(optionIds).size !== optionIds.length
    ) {
      return true;
    }
    if ((question.id === 'room_check') !== (question.kind === 'room_check')) return true;
  }
  const roomChecks = questions.filter((question) => question.kind === 'room_check');
  return (
    roomChecks.length > 1 ||
    roomChecks.some(
      (question) =>
        question.options.length > MAX_ROOM_SUGGESTIONS ||
        question.options.some(
          (option) => typeof option.roomTemplateId !== 'string' || option.roomTemplateId.trim().length === 0,
        ) ||
        new Set(question.options.map((option) => option.roomTemplateId)).size !== question.options.length,
    )
  );
}

/** Additive wire fields stay safe against an older cached response. */
function roomMeasurements(room: QuickQuoteRoom): QuickQuoteMeasurement[] {
  const candidate = (room as QuickQuoteRoom & { measurements?: unknown }).measurements;
  if (!Array.isArray(candidate)) return [];
  return candidate.filter((entry): entry is QuickQuoteMeasurement => {
    if (!entry || typeof entry !== 'object') return false;
    const value = entry as Partial<QuickQuoteMeasurement>;
    return (
      typeof value.serviceTemplateId === 'string' &&
      typeof value.name === 'string' &&
      typeof value.value === 'number' &&
      Number.isFinite(value.value) &&
      value.value > 0 &&
      (value.basis === 'area' || value.basis === 'fixture') &&
      (value.unit === 'square_feet' || value.unit === 'fixtures') &&
      (value.source === 'prompt' || value.source === 'answer')
    );
  });
}

/**
 * Text-first quote intake. Quick Quote shares the canonical quote engine with
 * other builders, but it is deliberately not a Magic Quote walkthrough: no
 * media controls, capture state, or Magic entitlement appears here.
 */
export default function QuickQuote({
  customerId,
  propertyId,
  segment,
  recoveryScope,
  manualQuoteHref,
  onInputStateChange,
  onRecoveryChange,
  onGenerated,
  onResumeContext,
}: QuickQuoteProps) {
  const { t } = useTranslation();
  const disclosure = useAiDisclosure();
  const context = useMemo(() => ({ customerId, propertyId, segment }), [customerId, propertyId, segment]);
  const [prompt, setPrompt] = useState('');
  const [session, setSession] = useState<QuickQuoteSession | null>(null);
  const [answers, setAnswers] = useState<Record<string, DraftAnswer>>({});
  const [coverageConfirmed, setCoverageConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingCreateId, setPendingCreateId] = useState(() => loadPendingQuickQuote(recoveryScope));
  const [activeSessionId, setActiveSessionId] = useState(() => loadActiveQuickQuote(recoveryScope));
  const [createAttempt, setCreateAttempt] = useState<QuickQuoteCreateInput | null>(null);
  const [answerAttempt, setAnswerAttempt] = useState<AnswerQuickQuoteInput | null>(null);
  const [generateAttempt, setGenerateAttempt] = useState<{ version: number; clientRequestId: string } | null>(null);
  const [mismatch, setMismatch] = useState<RecoveryMismatch | null>(null);
  const [resumeBusy, setResumeBusy] = useState(false);
  /**
   * Whether the create in flight is a replay of one whose fate was lost.
   *
   * The first attempt and its replay look identical to the mutation, and they
   * are not the same thing to the person waiting: one is an ordinary parse
   * starting, the other is a request that already went out being sent again
   * under its own id.
   */
  const [replayingCreate, setReplayingCreate] = useState(false);
  // Set in the click itself, before `ensure` resolves. The button's disabled
  // state only updates on the next render, so a second tap in the same turn
  // would otherwise mint a second request id and start a second parse.
  const consentLock = useRef(false);
  const [awaitingConsent, setAwaitingConsent] = useState(false);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState(0);
  const scopeKey = `${recoveryScope.companyId}:${recoveryScope.locationId ?? 'company'}`;
  const previousScopeKey = useRef(scopeKey);

  const capabilities = useQuery({
    queryKey: ['quick-quote', 'capabilities', recoveryScope.companyId, recoveryScope.locationId ?? 'company'],
    queryFn: quickQuoteApi.capabilities,
    retry: (failureCount, caught) => failureCount < 1 && isAmbiguous(caught),
    staleTime: 5 * 60_000,
  });
  const capabilityPayload = capabilities.data as unknown;
  const capability = quickQuoteCapabilityProtocol(capabilityPayload) ?? undefined;
  const capabilityProtocolError = capabilityPayload !== undefined && capability === undefined;
  const photosAvailable = Boolean(capability?.available && capability.photos.available);
  // Each photo is read the moment it arrives, so the AI notice comes first.
  const photoState = useQuickQuotePhotos(
    QUICK_QUOTE_MAX_PHOTOS,
    () => disclosure.ensure(true),
    photosAvailable && !session && !activeSessionId && !pendingCreateId,
  );

  useEffect(() => {
    if (previousScopeKey.current === scopeKey) return;
    previousScopeKey.current = scopeKey;
    setPrompt('');
    setSession(null);
    setAnswers({});
    setCoverageConfirmed(false);
    setRetryAfterSeconds(0);
    setError(null);
    setPendingCreateId(loadPendingQuickQuote(recoveryScope));
    setActiveSessionId(loadActiveQuickQuote(recoveryScope));
    setCreateAttempt(null);
    setAnswerAttempt(null);
    setGenerateAttempt(null);
    setMismatch(null);
    setResumeBusy(false);
    setRetryAfterSeconds(0);
  }, [recoveryScope, scopeKey]);

  useEffect(() => {
    if (retryAfterSeconds <= 0) return;
    const timer = window.setTimeout(() => setRetryAfterSeconds((seconds) => Math.max(0, seconds - 1)), 1_000);
    return () => window.clearTimeout(timer);
  }, [retryAfterSeconds]);

  function rememberRetryAfter(caught: unknown): void {
    if (
      caught instanceof ApiRequestError &&
      caught.status === 429 &&
      OPERATION_PRESERVING_LIMIT_CODES.has(caught.code)
    ) {
      setRetryAfterSeconds(Math.max(1, Math.ceil(caught.retryAfterSeconds ?? 1)));
    }
  }

  function adopt(created: QuickQuoteSession, pendingId?: string): void {
    if (!sameContext(created, context)) {
      setMismatch({
        source: pendingId ? 'pending' : 'active',
        pointerId: pendingId ?? created.id,
        session: created,
      });
      return;
    }
    if (pendingId) {
      clearPendingQuickQuote(recoveryScope, pendingId);
      setPendingCreateId((current) => (current === pendingId ? null : current));
    }
    rememberActiveQuickQuote(recoveryScope, created.id);
    setActiveSessionId(created.id);
    setSession(created);
    setAnswers({});
    setCoverageConfirmed(false);
    setRetryAfterSeconds(0);
    setError(null);
  }

  async function refreshAfterConflict(message: string): Promise<void> {
    if (!session) return;
    try {
      const latest = await quickQuoteApi.get(session.id);
      adopt(latest);
      setError(message);
    } catch (caught) {
      setError(apiMessage(caught, message));
    }
  }

  const start = useMutation({
    mutationFn: quickQuoteApi.start,
    onMutate: () => setError(null),
    onSuccess: (created, input) => {
      setRetryAfterSeconds(0);
      setCreateAttempt(null);
      // They belong to the Quick Quote now, which lists them itself.
      if (input.photoIds?.length) photoState.clear();
      adopt(created, input.clientRequestId);
    },
    onError: (caught, input) => {
      rememberRetryAfter(caught);
      if (isAmbiguous(caught)) {
        setCreateAttempt(input);
        setError(t('quickQuote.recovery.createUnknown'));
        return;
      }
      clearPendingQuickQuote(recoveryScope, input.clientRequestId);
      setPendingCreateId((current) => (current === input.clientRequestId ? null : current));
      setCreateAttempt(null);
      setError(apiMessage(caught, t('quickQuote.createError')));
    },
  });

  const answer = useMutation({
    mutationFn: (input: AnswerQuickQuoteInput) => quickQuoteApi.answer(session!.id, input),
    onMutate: () => setError(null),
    onSuccess: (updated) => {
      setRetryAfterSeconds(0);
      setAnswerAttempt(null);
      adopt(updated);
    },
    onError: async (caught) => {
      rememberRetryAfter(caught);
      if (requiresRefresh(caught)) {
        setAnswerAttempt(null);
        await refreshAfterConflict(apiMessage(caught, t('quickQuote.conflict')));
        return;
      }
      if (isAmbiguous(caught)) {
        setError(t('quickQuote.recovery.answerUnknown'));
        return;
      }
      setAnswerAttempt(null);
      setError(apiMessage(caught, t('quickQuote.answerError')));
    },
  });

  const generate = useMutation({
    mutationFn: (input: { version: number; clientRequestId: string }) => quickQuoteApi.generate(session!.id, input),
    onMutate: () => setError(null),
    onSuccess: (result: GenerateQuickQuoteResponse) => {
      setRetryAfterSeconds(0);
      if (typeof result.quoteId !== 'string' || !result.quoteId.trim()) {
        // No draft to open. Keep the exact operation id latched; a retry may
        // only replay this same generation, and the manual builder below
        // stays open as the way past it.
        setError(t('quickQuote.incompleteError'));
        return;
      }
      // A draft that exists is opened even if the server says it is missing a
      // room: the quote editor is the next step either way, and where that
      // room gets added by hand.
      setGenerateAttempt(null);
      clearActiveQuickQuote(recoveryScope, result.session.id);
      setActiveSessionId((current) => (current === result.session.id ? null : current));
      onGenerated(result.quoteId);
    },
    onError: async (caught) => {
      rememberRetryAfter(caught);
      if (requiresRefresh(caught)) {
        setGenerateAttempt(null);
        await refreshAfterConflict(apiMessage(caught, t('quickQuote.conflict')));
        return;
      }
      if (isAmbiguous(caught)) {
        setError(t('quickQuote.recovery.generateUnknown'));
        return;
      }
      setGenerateAttempt(null);
      setError(apiMessage(caught, t('quickQuote.generateError')));
    },
  });

  const activeRecovery = useQuery({
    queryKey: [
      'quick-quote',
      'recover-session',
      recoveryScope.companyId,
      recoveryScope.locationId ?? 'company',
      activeSessionId,
    ],
    queryFn: () => quickQuoteApi.get(activeSessionId!),
    enabled: !session && !mismatch && Boolean(activeSessionId),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const requestRecovery = useQuery({
    queryKey: [
      'quick-quote',
      'recover-request',
      recoveryScope.companyId,
      recoveryScope.locationId ?? 'company',
      pendingCreateId,
    ],
    queryFn: () => quickQuoteApi.byRequest(pendingCreateId!),
    enabled: !session && !mismatch && !activeSessionId && Boolean(pendingCreateId) && !start.isPending,
    retry: false,
    refetchInterval: (query) =>
      isApiError(query.state.error, 'QUICK_QUOTE_REQUEST_IN_PROGRESS') ||
      // An empty lookup for a create that may still be arriving resolves
      // itself: keep asking until it lands or the pointer settles below.
      (isApiError(query.state.error, RECOVERY_POINTER_UNKNOWN) &&
        pendingCreateId !== null &&
        !pendingQuickQuoteIsSettled(recoveryScope, pendingCreateId))
        ? 2_000
        : false,
  });

  useEffect(() => {
    if (activeRecovery.error) rememberRetryAfter(activeRecovery.error);
    // Error identity is the recovery event; the helper only updates local delay state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRecovery.error]);

  useEffect(() => {
    if (requestRecovery.error) rememberRetryAfter(requestRecovery.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestRecovery.error]);

  useEffect(() => {
    if (activeRecovery.data) adopt(activeRecovery.data);
    // `adopt` is intentionally driven only by a new server response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRecovery.data]);

  useEffect(() => {
    if (requestRecovery.data && pendingCreateId) adopt(requestRecovery.data, pendingCreateId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestRecovery.data, pendingCreateId]);

  useEffect(() => {
    if (!activeSessionId || !isApiError(activeRecovery.error, RECOVERY_POINTER_UNKNOWN)) return;
    clearActiveQuickQuote(recoveryScope, activeSessionId);
    setActiveSessionId((current) => (current === activeSessionId ? null : current));
  }, [activeRecovery.error, activeSessionId, recoveryScope]);

  useEffect(() => {
    if (!pendingCreateId || !isApiError(requestRecovery.error, RECOVERY_POINTER_UNKNOWN)) return;
    // The server has no row for this request, which is only proof it never
    // will once the create it names has had time to reach the server.
    if (!pendingQuickQuoteIsSettled(recoveryScope, pendingCreateId)) return;
    clearPendingQuickQuote(recoveryScope, pendingCreateId);
    setPendingCreateId((current) => (current === pendingCreateId ? null : current));
    setCreateAttempt(null);
    setError(t('quickQuote.recovery.notFound'));
  }, [pendingCreateId, recoveryScope, requestRecovery.error, t]);

  useEffect(() => {
    if (!session?.quoteId) return;
    clearActiveQuickQuote(recoveryScope, session.id);
    onGenerated(session.quoteId);
  }, [onGenerated, recoveryScope, session]);

  // Text, photos or both: a short description needs a photo that read cleanly.
  // An upload that failed may still have been stored and read, so it is tried
  // again or removed first rather than left out unseen.
  const canStart =
    !photoState.pending &&
    !photoState.restoreFailed &&
    photoState.failedUploads === 0 &&
    (prompt.trim().length >= QUICK_QUOTE_MIN_PROMPT_CHARS || (photosAvailable && photoState.readCount > 0));
  const questions = session?.questions ?? [];
  const roomCheck = questions.find((question) => question.id === 'room_check' && question.kind === 'room_check');
  const coverage = (
    session as (QuickQuoteSession & { coverage?: QuickQuoteSession['coverage']; followUpRoundsUsed?: number }) | null
  )?.coverage;
  // Quick has no legacy coverage payload. A mixed-version response that omits
  // this authority must fail closed instead of treating absence as consent.
  const hasIncludedRoom = session?.rooms.some((room) => room.excluded !== true) === true;
  const selectedRoomCheckOptionIds = roomCheck ? (answers[roomCheck.id]?.optionIds ?? []) : [];
  const selectedRoomSuggestion = Boolean(
    roomCheck?.options.some(
      (option) => typeof option.roomTemplateId === 'string' && selectedRoomCheckOptionIds.includes(option.id),
    ),
  );
  // A current or atomically selected included room is required even if a
  // mixed-version response mistakenly advertises confirmable coverage. An
  // all-excluded scope must never reach confirmation or generation.
  const coverageCanConfirm = coverage?.canConfirm === true && (hasIncludedRoom || selectedRoomSuggestion);
  const maxRounds = Math.min(
    MAX_CLARIFICATION_ROUNDS,
    capability?.limits.maxClarificationRounds ?? MAX_CLARIFICATION_ROUNDS,
  );
  const followUpRoundsUsed = (session as (QuickQuoteSession & { followUpRoundsUsed?: number }) | null)
    ?.followUpRoundsUsed;
  const followUpAtCap = followUpRoundsUsed === MAX_FOLLOW_UP_ROUNDS;
  const cappedRoomCheckHasDisabledSuggestions =
    followUpAtCap && roomCheck !== undefined && roomCheck.options.length > 0;
  const protocolError =
    roomCheckProtocolError(questions, capability?.limits.maxQuestionsPerRound ?? MAX_QUESTIONS) ||
    (session !== null &&
      (!coverage ||
        !Number.isInteger(session.clarificationRoundsUsed) ||
        session.clarificationRoundsUsed < 0 ||
        session.clarificationRoundsUsed > maxRounds ||
        !Number.isInteger(followUpRoundsUsed) ||
        followUpRoundsUsed! < 0 ||
        followUpRoundsUsed! > MAX_FOLLOW_UP_ROUNDS ||
        session.clarificationRoundsUsed > followUpRoundsUsed! ||
        (followUpAtCap &&
          questions.length > 0 &&
          (questions.at(-1)?.id !== 'room_check' || questions.at(-1)?.kind !== 'room_check')) ||
        ((session.clarificationRoundsUsed >= maxRounds || followUpAtCap) &&
          questions.some((question) => question.allowText))));
  const confirmedGenerationReady =
    !protocolError &&
    questions.length === 0 &&
    session?.status === 'ready' &&
    session.canGenerate === true &&
    coverage?.confirmed === true &&
    coverageCanConfirm;
  const staleReadyCoverage =
    !protocolError &&
    questions.length === 0 &&
    session?.status === 'ready' &&
    session.quoteId === null &&
    coverage?.confirmed === false;
  const questionlessBlocker =
    session !== null &&
    session.quoteId === null &&
    questions.length === 0 &&
    !confirmedGenerationReady &&
    !staleReadyCoverage &&
    session.status !== 'generating';
  const typedClarification = questions.some((question) =>
    questionNeedsClarificationCredits(question, answers[question.id]),
  );
  const clarificationAvailable = capability?.available === true;
  const existingActionsAvailable =
    capability?.available === true ||
    (capability?.available === false &&
      (capability.unavailableReason === 'ai_off' || capability.unavailableReason === 'unconfigured'));
  const showManualQuoteAction =
    protocolError ||
    !coverageCanConfirm ||
    questionlessBlocker ||
    !existingActionsAvailable ||
    cappedRoomCheckHasDisabledSuggestions ||
    // A request whose outcome is not known yet holds every input here; the
    // builder by hand is the way past that wait.
    answerAttempt !== null ||
    generateAttempt !== null;
  const operationLocked =
    !existingActionsAvailable ||
    answer.isPending ||
    generate.isPending ||
    answerAttempt !== null ||
    generateAttempt !== null;
  const retryAfterBlocked = retryAfterSeconds > 0;
  const anyAnswer = questions.some((question) => hasAnswer(answers[question.id]));
  const requiredAnswered = questions.every(
    (question) =>
      !question.required || hasAnswer(answers[question.id]) || (question.kind === 'room_check' && coverageConfirmed),
  );

  function setText(question: QuickQuoteQuestion, text: string): void {
    if (operationLocked || (followUpAtCap && question.kind === 'room_check')) return;
    setAnswers((current) => ({
      ...current,
      [question.id]: {
        optionIds: question.kind === 'room_check' ? (current[question.id]?.optionIds ?? []) : [],
        text,
      },
    }));
    if (question.kind === 'room_check') setCoverageConfirmed(false);
  }

  function setOption(question: QuickQuoteQuestion, optionId: string, checked: boolean): void {
    if (operationLocked || (followUpAtCap && question.kind === 'room_check')) return;
    setAnswers((current) => {
      const previous = current[question.id] ?? { optionIds: [], text: '' };
      const multiple = question.kind === 'multi_select' || question.kind === 'room_check';
      const optionIds = multiple
        ? checked
          ? [...new Set([...previous.optionIds, optionId])]
          : previous.optionIds.filter((id) => id !== optionId)
        : checked
          ? [optionId]
          : [];
      return {
        ...current,
        [question.id]: {
          ...previous,
          optionIds,
          text: question.kind === 'room_check' ? previous.text : '',
        },
      };
    });
    if (question.kind === 'room_check') setCoverageConfirmed(false);
  }

  // A photo still being prepared has no entry yet, so `busy` counts too. Any
  // request not yet known to have settled stays `submitting`: an ambiguous
  // failure keeps its attempt (or, for the first parse, its pending pointer)
  // until recovery decides it, and the server may still be acting on it. A
  // saved session still being loaded counts too, until it is adopted or
  // forgotten.
  // While the AI notice is still being answered nothing has been sent, so
  // what is on the form still counts as unsent.
  const inputState: QuickQuoteInputState =
    start.isPending || answer.isPending || generate.isPending
      ? 'submitting'
      : (!awaitingConsent && (answerAttempt !== null || generateAttempt !== null)) ||
          (!session && (pendingCreateId !== null || activeSessionId !== null))
        ? 'stuck'
        : session
          ? Object.values(answers).some(hasAnswer) || coverageConfirmed
            ? 'unsent'
            : 'clear'
          : prompt.trim().length > 0 || photoState.photos.length > 0 || photoState.busy
            ? 'unsent'
            : 'clear';
  useEffect(() => {
    onInputStateChange?.(inputState);
  }, [onInputStateChange, inputState]);
  const recoverySessionId = session?.id ?? activeSessionId;
  useEffect(() => {
    onRecoveryChange?.({ pendingCreateId, activeSessionId: recoverySessionId });
  }, [onRecoveryChange, pendingCreateId, recoverySessionId]);
  useEffect(() => () => onInputStateChange?.('clear'), [onInputStateChange]);

  function submitPrompt(): void {
    if (!canStart || start.isPending || pendingCreateId || consentLock.current) return;
    consentLock.current = true;
    setAwaitingConsent(true);
    const input: QuickQuoteCreateInput = {
      customerId,
      propertyId,
      segment,
      prompt: prompt.trim(),
      ...(photoState.readyIds.length > 0 ? { photoIds: photoState.readyIds } : {}),
      locale: activeLanguage(),
      clientRequestId: newRequestId(),
    };
    void disclosure
      .ensure(true)
      .then(() => {
        // Only after they accept. Installing the recovery pointer first left a
        // cancelled notice polling a create that was never sent, and the
        // pending id blocked another try until that poll expired.
        rememberPendingQuickQuote(recoveryScope, input.clientRequestId);
        setPendingCreateId(input.clientRequestId);
        setCreateAttempt(input);
        setReplayingCreate(false);
        start.mutate(input);
      })
      .catch(() => undefined)
      .finally(() => {
        consentLock.current = false;
        setAwaitingConsent(false);
      });
  }

  /**
   * The AI notice comes before a later-round request. Until it is accepted the
   * request has not been sent, so the attempt does not lock the page, and a
   * declined notice drops it rather than leaving it latched as if its fate
   * were unknown.
   */
  function askThenSend(send: () => void, declined: () => void): void {
    setAwaitingConsent(true);
    void disclosure
      .ensure(true)
      .then(send)
      .catch(declined)
      .finally(() => setAwaitingConsent(false));
  }

  function submitAnswers(): void {
    if (!session || operationLocked || protocolError) return;
    const submitted: QuickQuoteAnswer[] = questions.flatMap((question) => {
      const draft = answers[question.id];
      if (!hasAnswer(draft)) return [];
      return [
        {
          questionId: question.id,
          ...(draft!.optionIds.length > 0 ? { optionIds: draft!.optionIds } : {}),
          ...(draft!.text.trim() ? { text: draft!.text.trim() } : {}),
        },
      ];
    });
    const input: AnswerQuickQuoteInput = {
      version: session.version,
      answers: submitted,
      ...(coverageConfirmed ? { coverageConfirmed: true } : {}),
      clientRequestId: newRequestId(),
    };
    setAnswerAttempt(input);
    askThenSend(
      () => answer.mutate(input),
      () => setAnswerAttempt(null),
    );
  }

  function reviewUpdatedRoomCheck(): void {
    if (!session || operationLocked || !staleReadyCoverage) return;
    // Recovery GETs stay read-only with respect to aggregate version. This
    // explicit, idempotent answers operation asks the server to materialize an
    // unconfirmed Room Check from fresh relational coverage. The user then
    // confirms that new version without another model call or credit charge.
    const input: AnswerQuickQuoteInput = {
      version: session.version,
      answers: [],
      coverageConfirmed: true,
      clientRequestId: newRequestId(),
    };
    setAnswerAttempt(input);
    askThenSend(
      () => answer.mutate(input),
      () => setAnswerAttempt(null),
    );
  }

  function retryCreate(): void {
    if (!createAttempt || start.isPending || retryAfterBlocked) return;
    setReplayingCreate(true);
    void disclosure
      .ensure(true)
      .then(() => start.mutate(createAttempt))
      .catch(() => undefined);
  }

  function retryAnswer(): void {
    if (answerAttempt && !answer.isPending && !retryAfterBlocked) {
      void disclosure
        .ensure(true)
        .then(() => answer.mutate(answerAttempt))
        .catch(() => undefined);
    }
  }

  function retryGenerate(): void {
    if (generateAttempt && !generate.isPending && !retryAfterBlocked) {
      void disclosure
        .ensure(true)
        .then(() => generate.mutate(generateAttempt))
        .catch(() => undefined);
    }
  }

  async function resumeMismatch(): Promise<void> {
    if (!mismatch || resumeBusy) return;
    setResumeBusy(true);
    try {
      await onResumeContext({
        customerId: mismatch.session.customerId,
        propertyId: mismatch.session.propertyId,
        segment: mismatch.session.segment,
      });
    } catch (caught) {
      setError(apiMessage(caught, t('quickQuote.recovery.resumeError')));
      setResumeBusy(false);
    }
  }

  /**
   * Retire a pointer the server has answered conclusively about. The person
   * asked for this: the alert says what the check found before offering it.
   */
  function forgetPendingCheck(): void {
    if (!pendingCreateId) return;
    clearPendingQuickQuote(recoveryScope, pendingCreateId);
    setPendingCreateId(null);
    setCreateAttempt(null);
    setError(null);
  }

  function forgetActiveRecovery(): void {
    if (!activeSessionId) return;
    clearActiveQuickQuote(recoveryScope, activeSessionId);
    setActiveSessionId(null);
    setError(null);
  }

  function forgetMismatch(): void {
    if (!mismatch) return;
    if (mismatch.source === 'pending') {
      clearPendingQuickQuote(recoveryScope, mismatch.pointerId);
      setPendingCreateId((current) => (current === mismatch.pointerId ? null : current));
    } else {
      clearActiveQuickQuote(recoveryScope, mismatch.pointerId);
      setActiveSessionId((current) => (current === mismatch.pointerId ? null : current));
    }
    setMismatch(null);
  }

  return (
    <section className="card mb-6 border-[color:var(--color-brand)]" aria-labelledby="quick-quote-heading">
      {disclosure.dialog}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-brand)]">
            {t('quickQuote.eyebrow')}
          </p>
          <h2 id="quick-quote-heading" className="text-lg font-semibold">
            Quick Quote
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-muted)]">{t('quickQuote.description')}</p>
        </div>
        <span className="rounded-full border border-[color:var(--color-line)] px-2 py-1 text-xs font-semibold">
          {t('quickQuote.starterBadge')}
        </span>
      </div>

      {error ? (
        <div className="mb-4">
          <Alert tone="error" title={t('quickQuote.errorTitle')}>
            {error}
            {/* A red message is never the end of the road: the manual builder
                keeps this customer and property. */}
            <div className="mt-2">
              <a className="font-semibold underline" href={manualQuoteHref}>
                {t('quickQuote.manualAction')}
              </a>
            </div>
          </Alert>
        </div>
      ) : null}

      {retryAfterBlocked ? (
        <p className="mb-4 text-sm text-[color:var(--color-muted)]" role="status">
          {t('quickQuote.recovery.retryAfter', { count: retryAfterSeconds })}
        </p>
      ) : null}

      {mismatch ? (
        <Alert tone="warning" title={t('quickQuote.recovery.differentContextTitle')}>
          <p>{t('quickQuote.recovery.differentContextBody')}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="primary" busy={resumeBusy} onClick={() => void resumeMismatch()}>
              {t('quickQuote.recovery.resume')}
            </Button>
            <Button disabled={resumeBusy} onClick={forgetMismatch}>
              {t('quickQuote.recovery.forget')}
            </Button>
          </div>
        </Alert>
      ) : !session && activeSessionId ? (
        activeRecovery.isError && !isApiError(activeRecovery.error, RECOVERY_POINTER_UNKNOWN) ? (
          <Alert tone="warning" title={t('quickQuote.recovery.loadErrorTitle')}>
            <p>{apiMessage(activeRecovery.error, t('quickQuote.recovery.loadError'))}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button disabled={retryAfterBlocked} onClick={() => void activeRecovery.refetch()}>
                {t('quickQuote.recovery.checkAgain')}
              </Button>
              {isConclusiveRecoveryRefusal(activeRecovery.error) ? (
                <Button onClick={forgetActiveRecovery}>{t('quickQuote.recovery.forget')}</Button>
              ) : null}
              <a className="btn btn-secondary" href={manualQuoteHref}>
                {t('quickQuote.manualAction')}
              </a>
            </div>
          </Alert>
        ) : (
          <LoadingBlock label={t('quickQuote.recovery.loadingSession')} />
        )
      ) : !session && start.isPending ? (
        // Nothing is unknown yet: this request is in flight in this tab. The
        // recovery alert below — with its "check again" and "retry the exact
        // request" — is for a submission whose fate was lost, and showing it
        // the moment someone presses the button tells them their first parse
        // already went wrong.
        <LoadingBlock label={replayingCreate ? t('quickQuote.recovery.submitting') : t('quickQuote.parsingPrompt')} />
      ) : !session && pendingCreateId ? (
        <Alert tone="info" title={t('quickQuote.recovery.checkingTitle')}>
          <p>
            {isApiError(requestRecovery.error, 'QUICK_QUOTE_REQUEST_IN_PROGRESS') ||
            (isApiError(requestRecovery.error, RECOVERY_POINTER_UNKNOWN) &&
              pendingCreateId !== null &&
              !pendingQuickQuoteIsSettled(recoveryScope, pendingCreateId))
              ? t('quickQuote.recovery.stillWorking')
              : requestRecovery.isError
                ? apiMessage(requestRecovery.error, t('quickQuote.recovery.checkError'))
                : t('quickQuote.recovery.checking')}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button disabled={retryAfterBlocked} onClick={() => void requestRecovery.refetch()}>
              {t('quickQuote.recovery.checkAgain')}
            </Button>
            {createAttempt ? (
              <Button disabled={retryAfterBlocked} onClick={retryCreate}>
                {t('quickQuote.recovery.retrySame')}
              </Button>
            ) : null}
            {isConclusiveRecoveryRefusal(requestRecovery.error) ? (
              <Button onClick={forgetPendingCheck}>{t('quickQuote.recovery.forget')}</Button>
            ) : null}
            <a className="btn btn-secondary" href={manualQuoteHref}>
              {t('quickQuote.manualAction')}
            </a>
          </div>
        </Alert>
      ) : !session && capabilities.isPending ? (
        <LoadingBlock label={t('quickQuote.checkingAvailability')} />
      ) : !session && (capabilities.isError || !capability) ? (
        <>
          <Alert tone="warning" title={t('quickQuote.unavailableTitle')}>
            {capabilityProtocolError
              ? t('quickQuote.capabilityProtocolError')
              : apiMessage(capabilities.error, t('quickQuote.unavailable.generic'))}
          </Alert>
          <a className="btn btn-secondary mt-3" href={manualQuoteHref}>
            {t('quickQuote.manualAction')}
          </a>
        </>
      ) : !session && capability && !capability.available ? (
        <Alert tone="info" title={t('quickQuote.unavailableTitle')}>
          {t(`quickQuote.unavailable.${capability.unavailableReason ?? 'generic'}`)}
        </Alert>
      ) : !session && capability ? (
        <>
          <TextArea
            label={t('quickQuote.promptLabel')}
            hint={t('quickQuote.promptHint')}
            placeholder={t('quickQuote.promptPlaceholder')}
            rows={5}
            maxLength={capability.limits.promptChars}
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
          />
          {photosAvailable ? (
            <QuickQuotePhotoPicker
              state={photoState}
              maxPhotos={capability.photos.maxPhotos}
              disabled={start.isPending || awaitingConsent}
            />
          ) : null}
          <div className="mb-4 rounded-md bg-[color:var(--color-surface)] p-3 text-sm">
            <p className="font-medium">{t('quickQuote.shorthandTitle')}</p>
            <p className="text-[color:var(--color-muted)]">{t('quickQuote.shorthandBody')}</p>
            {/* Only onto an empty box: a stray tap must not replace what was typed. */}
            <Button
              className="mt-2 text-sm"
              disabled={prompt.trim().length > 0}
              onClick={() => setPrompt(t('quickQuote.samplePrompt'))}
            >
              {t('quickQuote.useSample')}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              busy={start.isPending || awaitingConsent}
              disabled={!canStart || awaitingConsent}
              onClick={submitPrompt}
            >
              {t('quickQuote.start')}
            </Button>
            <p className="text-xs text-[color:var(--color-muted)]">
              {t('quickQuote.costNote', {
                initial: capability.costs.initialParse,
                clarification: capability.costs.freeTextClarification,
              })}
            </p>
          </div>
          {photoState.pending ? (
            <p className="mt-2 text-xs text-[color:var(--color-muted)]" role="status">
              {t('quickQuote.photos.waitForReads')}
            </p>
          ) : photoState.restoreFailed ? (
            <p className="mt-2 text-xs text-[color:var(--color-muted)]" role="status">
              {t('quickQuote.photos.restoreFailed')}
            </p>
          ) : photoState.failedUploads > 0 ? (
            <p className="mt-2 text-xs text-[color:var(--color-muted)]" role="status">
              {t('quickQuote.photos.resolveFailed')}
            </p>
          ) : photosAvailable &&
            photoState.readyIds.length > 0 &&
            photoState.readCount === 0 &&
            prompt.trim().length < QUICK_QUOTE_MIN_PROMPT_CHARS ? (
            <p className="mt-2 text-xs text-[color:var(--color-muted)]" role="status">
              {t('quickQuote.photos.noneRead')}
            </p>
          ) : null}
        </>
      ) : session ? (
        <>
          {capabilityProtocolError ? (
            <Alert tone="warning">{t('quickQuote.capabilityProtocolError')}</Alert>
          ) : capabilities.isPending ? (
            <Alert tone="info">{t('quickQuote.checkingAvailability')}</Alert>
          ) : capabilities.isError || !capability ? (
            <Alert tone="warning">{t('quickQuote.existingAvailabilityUnknown')}</Alert>
          ) : !capability.available ? (
            <Alert tone={existingActionsAvailable ? 'info' : 'warning'}>
              {existingActionsAvailable ? t('quickQuote.existingUnavailable') : t('quickQuote.existingLocked')}
            </Alert>
          ) : null}

          {session.summary ? <p className="mb-4 mt-3 text-sm">{session.summary}</p> : null}

          <QuickQuoteSessionPhotos photos={session.photos ?? []} />

          <PropertyFacts session={session} />

          <div className="mb-5 rounded-md border border-[color:var(--color-line)] p-3">
            <h3 className="mb-2 font-semibold">{t('quickQuote.roomsTitle')}</h3>
            {session.rooms.length > 0 ? (
              <ul className="grid gap-2 sm:grid-cols-2">
                {session.rooms.map((room) => (
                  <li key={room.id} className="flex items-start justify-between gap-2 text-sm">
                    <div>
                      <span className={room.excluded === true ? 'text-[color:var(--color-muted)]' : undefined}>
                        {room.quantity > 1 ? `${room.quantity}× ` : ''}
                        {room.name}
                      </span>
                      {room.excluded === true ? (
                        <span
                          className={classes(
                            'ml-2 rounded-full border border-[color:var(--color-line)]',
                            'px-2 py-0.5 text-xs text-[color:var(--color-muted)]',
                          )}
                        >
                          {t('quickQuote.roomExcluded')}
                        </span>
                      ) : null}
                      {roomMeasurements(room).length > 0 ? (
                        <ul className="mt-1 text-xs text-[color:var(--color-muted)]">
                          {roomMeasurements(room).map((measurement) => (
                            <li key={`${measurement.serviceTemplateId}:${measurement.basis}`}>
                              {measurement.name}: {new Intl.NumberFormat(session.locale).format(measurement.value)}{' '}
                              {t(`quickQuote.measurementUnit.${measurement.unit}`, {
                                count: measurement.value,
                              })}
                              {' · '}
                              {t(`quickQuote.measurementSource.${measurement.source}`)}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                    <span className="text-xs text-[color:var(--color-muted)]">
                      {t(`quickQuote.roomSource.${room.source}`)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-[color:var(--color-muted)]">{t('quickQuote.noRoomsYet')}</p>
            )}
          </div>

          {(coverage?.unresolved.length ?? 0) > 0 ? (
            <div className="mb-5 rounded-md border border-[color:var(--color-warning)]/40 p-3">
              <h3 className="font-semibold">{t('quickQuote.coverageGapsTitle')}</h3>
              <ul className="mt-2 list-disc pl-5 text-sm text-[color:var(--color-muted)]">
                {coverage!.unresolved.map((gap) => (
                  <li key={gap}>{gap}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {!coverageCanConfirm ? (
            <div className="mb-5">
              <Alert tone="warning" title={t('quickQuote.coverageBlockedTitle')}>
                {t('quickQuote.coverageBlockedBody')}
              </Alert>
            </div>
          ) : null}

          {protocolError ? (
            <Alert tone="error" title={t('quickQuote.protocolErrorTitle')}>
              {t('quickQuote.protocolErrorBody')}
            </Alert>
          ) : questions.length > 0 ? (
            <div className="mb-5">
              <h3 className="mb-1 font-semibold">{t('quickQuote.questionsTitle')}</h3>
              <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('quickQuote.questionsHint')}</p>

              <div className="grid gap-5">
                {questions.map((question) => {
                  const draft = answers[question.id] ?? { optionIds: [], text: '' };
                  if (question.kind === 'number' || question.kind === 'short_text') {
                    const clarificationField = question.kind === 'short_text';
                    return (
                      <TextInput
                        key={question.id}
                        label={question.prompt}
                        hint={clarificationField ? t('quickQuote.correctionHint') : undefined}
                        type="text"
                        inputMode={question.kind === 'number' ? 'decimal' : undefined}
                        required={question.required}
                        disabled={operationLocked || (clarificationField && !clarificationAvailable)}
                        maxLength={capability?.limits.answerTextChars ?? 1_000}
                        value={draft.text}
                        onChange={(event) => setText(question, event.target.value)}
                      />
                    );
                  }

                  const multiple = question.kind === 'multi_select' || question.kind === 'room_check';
                  return (
                    <fieldset key={question.id} className="rounded-md border border-[color:var(--color-line)] p-3">
                      <legend className="px-1 font-medium">{question.prompt}</legend>
                      {question.options.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {question.options.map((option) => {
                            const selected = draft.optionIds.includes(option.id);
                            return (
                              <label
                                key={option.id}
                                className={classes(
                                  'flex cursor-pointer items-center gap-2 rounded-full border px-3 py-2 text-sm',
                                  selected
                                    ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]'
                                    : 'border-[color:var(--color-line)]',
                                )}
                              >
                                <input
                                  type={multiple ? 'checkbox' : 'radio'}
                                  name={`quick-quote-${session.id}-${question.id}`}
                                  value={option.id}
                                  checked={selected}
                                  disabled={operationLocked || (followUpAtCap && question.kind === 'room_check')}
                                  onChange={(event) => setOption(question, option.id, event.target.checked)}
                                />
                                <span>{option.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      ) : null}

                      {question.allowText ? (
                        <div className="mt-3">
                          <TextInput
                            label={
                              question.kind === 'room_check'
                                ? t('quickQuote.roomCorrectionLabel')
                                : t('quickQuote.correctionLabel')
                            }
                            hint={t('quickQuote.correctionHint')}
                            disabled={operationLocked || followUpAtCap || !clarificationAvailable}
                            maxLength={capability?.limits.answerTextChars ?? 1_000}
                            value={draft.text}
                            onChange={(event) => setText(question, event.target.value)}
                          />
                        </div>
                      ) : null}
                    </fieldset>
                  );
                })}
              </div>

              {roomCheck ? (
                <label className="mt-4 flex items-start gap-2 rounded-md bg-[color:var(--color-surface)] p-3 text-sm">
                  <input
                    className="mt-1"
                    type="checkbox"
                    disabled={
                      operationLocked ||
                      !coverageCanConfirm ||
                      (session.rooms.length === 0 && !hasAnswer(answers[roomCheck.id]))
                    }
                    checked={coverageConfirmed}
                    onChange={(event) => setCoverageConfirmed(event.target.checked)}
                  />
                  <span>
                    <strong className="block">{t('quickQuote.coverageTitle')}</strong>
                    {t('quickQuote.coverageHint')}
                  </span>
                </label>
              ) : null}

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button
                  variant="primary"
                  busy={answer.isPending}
                  disabled={
                    operationLocked ||
                    (!anyAnswer && !coverageConfirmed) ||
                    !requiredAnswered ||
                    (typedClarification && !clarificationAvailable)
                  }
                  onClick={submitAnswers}
                >
                  {answerAttempt ? t('quickQuote.retryAnswers') : t('quickQuote.continue')}
                </Button>
                <span className="text-xs text-[color:var(--color-muted)]">
                  {typedClarification
                    ? t('quickQuote.typedAnswerCost', {
                        count: capability?.costs.freeTextClarification ?? 2,
                      })
                    : t('quickQuote.structuredAnswersFree')}
                </span>
                {answerAttempt && !answer.isPending ? (
                  <Button disabled={retryAfterBlocked} onClick={retryAnswer}>
                    {t('quickQuote.recovery.retrySame')}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}

          {showManualQuoteAction ? (
            <a className="btn btn-secondary mb-5" href={manualQuoteHref}>
              {t('quickQuote.manualAction')}
            </a>
          ) : null}

          {confirmedGenerationReady ? (
            <div className="rounded-md border border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] p-4">
              <h3 className="font-semibold">{t('quickQuote.readyTitle')}</h3>
              <p className="mb-3 text-sm">{t('quickQuote.readyHint')}</p>
              <Button
                variant="primary"
                busy={generate.isPending || session.status === 'generating'}
                disabled={!existingActionsAvailable || (generateAttempt !== null && !generate.isPending)}
                onClick={() => {
                  if (generate.isPending || generateAttempt) return;
                  const input = { version: session.version, clientRequestId: newRequestId() };
                  setGenerateAttempt(input);
                  askThenSend(
                    () => generate.mutate(input),
                    () => setGenerateAttempt(null),
                  );
                }}
              >
                {t('quickQuote.generate')}
              </Button>
              {generateAttempt && !generate.isPending ? (
                <Button className="ml-2" disabled={retryAfterBlocked} onClick={retryGenerate}>
                  {t('quickQuote.recovery.retrySame')}
                </Button>
              ) : null}
            </div>
          ) : staleReadyCoverage ? (
            <div className="rounded-md border border-[color:var(--color-warning)] bg-[color:var(--color-surface)] p-4">
              <h3 className="font-semibold">{t('quickQuote.updatedRoomCheckTitle')}</h3>
              <p className="mb-3 text-sm">{t('quickQuote.updatedRoomCheckBody')}</p>
              <Button
                variant="primary"
                busy={answer.isPending}
                disabled={operationLocked}
                onClick={reviewUpdatedRoomCheck}
              >
                {t('quickQuote.reviewUpdatedRoomCheck')}
              </Button>
              {answerAttempt && !answer.isPending ? (
                <Button className="ml-2" disabled={retryAfterBlocked} onClick={retryAnswer}>
                  {t('quickQuote.recovery.retrySame')}
                </Button>
              ) : null}
              <span className="ml-3 text-xs text-[color:var(--color-muted)]">
                {t('quickQuote.structuredAnswersFree')}
              </span>
            </div>
          ) : questions.length === 0 ? (
            <p className="text-sm text-[color:var(--color-muted)]" role="status">
              {t('quickQuote.waitingForNextCheck')}
            </p>
          ) : null}

          {session.billedCredits > 0 ? (
            <p className="mt-3 text-xs text-[color:var(--color-muted)]">
              {t('quickQuote.creditsUsed', { count: session.billedCredits })}
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function PropertyFacts({ session }: { session: QuickQuoteSession }) {
  const { t } = useTranslation();
  const number = new Intl.NumberFormat(session.locale);
  const facts = [
    session.propertyFacts.bedrooms !== null
      ? { label: t('quickQuote.fact.bedrooms'), value: number.format(session.propertyFacts.bedrooms) }
      : null,
    session.propertyFacts.bathrooms !== null
      ? { label: t('quickQuote.fact.bathrooms'), value: number.format(session.propertyFacts.bathrooms) }
      : null,
    session.propertyFacts.squareFeet !== null
      ? { label: t('quickQuote.fact.squareFeet'), value: number.format(session.propertyFacts.squareFeet) }
      : null,
    session.propertyFacts.frequency
      ? {
          label: t('quickQuote.fact.frequency'),
          value:
            session.propertyFacts.frequency === 'weekly' && (session.propertyFacts.visitsPerWeek ?? 1) > 1
              ? t('quickQuote.frequency.visitsPerWeek', { count: session.propertyFacts.visitsPerWeek })
              : t(`quickQuote.frequency.${session.propertyFacts.frequency}`),
        }
      : null,
    session.propertyFacts.condition
      ? {
          label: t('quickQuote.fact.condition'),
          value: t(`quickQuote.condition.${session.propertyFacts.condition}`),
        }
      : null,
  ].filter((entry): entry is { label: string; value: string } => entry !== null);

  if (
    facts.length === 0 &&
    session.propertyFacts.assumptions.length === 0 &&
    session.propertyFacts.unrecognized.length === 0
  ) {
    return null;
  }

  return (
    <div className="mb-5 rounded-md bg-[color:var(--color-surface)] p-3">
      <h3 className="font-semibold">{t('quickQuote.factsTitle')}</h3>
      {facts.length > 0 ? (
        <dl className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          {facts.map((fact) => (
            <div key={fact.label} className="flex justify-between gap-3">
              <dt className="text-[color:var(--color-muted)]">{fact.label}</dt>
              <dd className="font-medium">{fact.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {session.propertyFacts.assumptions.length > 0 ? (
        <div className="mt-3">
          <h4 className="text-sm font-semibold">{t('quickQuote.assumptionsTitle')}</h4>
          <ul className="list-disc pl-5 text-sm text-[color:var(--color-muted)]">
            {session.propertyFacts.assumptions.map((assumption) => (
              <li key={assumption}>{assumption}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {session.propertyFacts.unrecognized.length > 0 ? (
        <div className="mt-3">
          <h4 className="text-sm font-semibold">{t('quickQuote.unrecognizedTitle')}</h4>
          <ul className="list-disc pl-5 text-sm text-[color:var(--color-muted)]">
            {session.propertyFacts.unrecognized.map((entry) => (
              <li key={entry}>{entry}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
